import { useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Building2, Globe2, UserRound } from "lucide-react";
import { useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { api, errorMessage, unwrap, setActiveOrganization } from "../api/client";
import { useOrg } from "../api/org";
import { useAuth } from "../auth/auth";
import { useSaveName } from "../components/NamePrompt";
import { Alert, Button, Field, Select, TextInput } from "../components/ui";
import { normalizeOrigin, originProblem } from "../lib/snippets";
import { landingUrl } from "../config";
import { useT, useIntlShape } from "../i18n/I18nProvider";
import { DirIcon } from "../i18n/mirror";

export const COUNTRIES = [
  { value: "US", currency: "USD" },
  { value: "CA", currency: "CAD" },
  { value: "GB", currency: "GBP" },
  { value: "AU", currency: "AUD" },
  { value: "NZ", currency: "NZD" },
  { value: "IE", currency: "EUR" },
  { value: "DE", currency: "EUR" },
  { value: "NL", currency: "EUR" },
  { value: "ES", currency: "EUR" },
  { value: "IT", currency: "EUR" },
  { value: "FR", currency: "EUR" },
  { value: "MX", currency: "MXN" },
] as const;

export function Onboarding() {
  const t = useT();
  const intl = useIntlShape();
  const { orgId, me, selectOrg } = useOrg();
  const [params] = useSearchParams();
  const another = params.has("new");
  const { signOut } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  // the sign-in gave no name (External ID without a display name): ask for it here once
  const needsName = !me.user.display_name;
  const [person, setPerson] = useState("");
  const saveName = useSaveName();
  const [country, setCountry] = useState<string>("US");
  const [website, setWebsite] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // `?new` makes another company for a user who has one already (company switcher).
  if (orgId && !busy && !another) return <Navigate to="/get-started" replace />;

  const origin = normalizeOrigin(website);
  const websiteProblem = website.trim() && originProblem(origin) ? t("auth.onboarding.websiteInvalid", { origin }) : null;
  const regions = new Intl.DisplayNames([intl.locale], { type: "region" });
  const countries = COUNTRIES.map((c) => ({ value: c.value, label: regions.of(c.value) ?? c.value })).sort((a, b) => a.label.localeCompare(b.label, intl.locale));

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      if (needsName && person.trim()) await saveName.mutateAsync(person);
      const currency = COUNTRIES.find((c) => c.value === country)?.currency;
      const org = await unwrap(
        api.POST("/v1/orgs", { body: { name: name.trim(), country, default_jurisdiction: country, currency } }),
      );
      const path = { org_id: org.id };
      setActiveOrganization(org.id); // the requests below are about the new company
      // The live key for the website (its origin when given; more can be added on Get started).
      await unwrap(
        api.POST("/v1/orgs/{org_id}/embed-keys", {
          params: { path },
          body: { label: t("auth.onboarding.keyLabel"), mode: "live", allowed_origins: origin && !websiteProblem ? [origin] : [] },
        }),
      );
      // The calculator shows the company's name and contact from the start.
      try {
        const settings = await unwrap(api.GET("/v1/orgs/{org_id}/settings", { params: { path } }));
        const brand = settings.documents.brand;
        await unwrap(
          api.PUT("/v1/orgs/{org_id}/settings/{kind}", {
            params: { path: { ...path, kind: "brand" } },
            body: {
              document: {
                ...brand,
                company_name: name.trim(),
                contact: { ...brand.contact, email: brand.contact.email || me.user.email, url: origin || brand.contact.url },
              },
            },
          }),
        );
      } catch {
        /* branding can be set later */
      }
      selectOrg(org.id);
      await qc.invalidateQueries({ queryKey: ["me"] });
      navigate("/get-started", { replace: true });
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-art">
        <a href={landingUrl()} className="brand" style={{ padding: 0 }} title={t("shell.brand.website")}>
          <img src="/solaros-mark.png" alt="" />
          <div>
            <b>SolarOS</b>
          </div>
        </a>
        <div>
          <h2>{intl.formatMessage({ id: "auth.onboarding.headline" }, { em: (c) => <span className="gradient-text">{c}</span> })}</h2>
          <p>{t("auth.onboarding.body")}</p>
        </div>
        <img className="roof" src="/roof-design.webp" alt="" />
      </div>
      <div className="auth-form">
        <form
          className="auth-card"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <div>
            <span className="badge info plain">{t("auth.onboarding.step", { step: 1, total: 2 })}</span>
            <h1 style={{ marginTop: 12 }}>{t("auth.onboarding.title")}</h1>
            <p className="muted" style={{ marginTop: 6 }}>
              {intl.formatMessage({ id: "auth.onboarding.signedInAs" }, { email: me.user.email, b: (c) => <b dir="auto">{c}</b> })}
            </p>
          </div>
          {error && <Alert tone="danger">{error}</Alert>}
          {needsName && (
            <Field label={<><UserRound size={14} /> {t("auth.name.label")}</>} hint={t("auth.name.hint")}>
              <TextInput required maxLength={100} value={person} onChange={(e) => setPerson(e.target.value)} placeholder={t("auth.name.placeholder")} autoComplete="name" />
            </Field>
          )}
          <Field label={<><Building2 size={14} /> {t("auth.onboarding.companyName")}</>}>
            <TextInput required maxLength={200} value={name} onChange={(e) => setName(e.target.value)} placeholder={t("auth.onboarding.companyPlaceholder")} autoFocus />
          </Field>
          <Field label={t("auth.onboarding.country")}>
            <Select value={country} onChange={setCountry} options={countries} />
          </Field>
          <Field
            label={<><Globe2 size={14} /> {t("auth.onboarding.website")}</>}
            hint={t("auth.onboarding.websiteHint")}
            error={websiteProblem}
          >
            <TextInput value={website} onChange={(e) => setWebsite(e.target.value)} placeholder={t("auth.onboarding.websitePlaceholder")} inputMode="url" dir="ltr" />
          </Field>
          <Button type="submit" variant="primary" size="lg" block loading={busy} disabled={!name.trim() || !!websiteProblem || (needsName && !person.trim())} icon={<DirIcon><ArrowRight /></DirIcon>}>
            {t("auth.onboarding.create")}
          </Button>
          <p className="small faint">
            {t("auth.onboarding.wrongAccount")}{" "}
            <button type="button" className="link-btn" onClick={() => signOut()}>
              {t("shell.signOut")}
            </button>
          </p>
        </form>
      </div>
    </div>
  );
}
